import { css, jsx } from '@emotion/core';
import React, { useEffect, useState } from 'react';

import DesignPage from '@/src/cms/models/DesignPage';
import RegisterForm from '@/src/register/components/RegisterForm';
import RegisterHero from '@/src/register/components/RegisterHero';
import AdminLink from '@/src/shared/components/AdminLink';
import Breadcrumbs from '@/src/shared/components/Breadcrumbs';
import Container from '@/src/shared/components/Container';
import Layout from '@/src/shared/components/Layout';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { bp } from '@/styles';
import request from '@/utils/axios';

const blockIdMap = {
  fullWidthParagraph1: 38,
  fullWidthParagraph2: 39,
};

const styles = {
  container: css`
    @media ${bp.md} {
      display: flex;
    }
  `,
  content: css`
    margin: 0 0 3.2rem;

    @media ${bp.md} {
      flex: 1 1 37rem;
      margin: 0;
      padding: 0 3.2rem 0 0;
    }
  `,
  form: css`
    @media ${bp.md} {
      flex: 1 1 auto;
    }
  `,
};

const Register = props => {
  const { messageData = {} } = props;

  const [optionsData, setOptionsData] = useState({
    Prefixes: [],
    Countries: [],
    States: [],
    SecurityQuestions: [],
  });
  const [pagedata, setPageData] = useState({});
  const [loading, setLoading] = useState(true);

  const { Prefixes, Countries, States } = optionsData;

  const fetchPageData = async () => {
    const pageResponse = await request({
      url: `cms/designpage`,
      params: {
        alias: 'register',
      },
    });
    setPageData(pageResponse.data);
    const response = await request({
      url: 'profile/registrationoptions',
    });
    if (response.data && response.data.FieldOptions) {
      const { States: stateOptions } = response.data.FieldOptions;
      const otherIdx = stateOptions.findIndex(({ StateCode }) => StateCode === 'Other');
      const other = otherIdx > -1 ? stateOptions.splice(otherIdx, 1) : [];
      setOptionsData({
        ...response.data.FieldOptions,
        States: [...stateOptions, ...other],
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchPageData();
  }, []);

  if (loading) {
    return null;
  }

  const designPageData = new DesignPage({ ...pagedata, ...messageData });

  const paragraph2 = designPageData.getBlockDataById(blockIdMap.fullWidthParagraph2);

  const componentParagraph1 = designPageData.getComponentDataById(54);
  const componentParagraph2 = designPageData.getComponentDataById(55);

  const fieldInfo = [
    [
      {
        label: 'Prefix',
        required: false,
        persist: true,
        type: 'dropdown',
        options: Prefixes,
        optionId: 'PrefixId',
        optionDesc: 'PrefixDescription',
        field: 'Prefix',
      },
      {
        label: 'First Name',
        required: true,
        persist: true,
        type: 'input',
        field: 'First',
        formOverrides: {
          rules: [{ required: true, message: 'Enter First Name.' }],
        },
      },
      {
        label: 'Middle Initial',
        required: true,
        persist: true,
        type: 'input',
        field: 'Middle',
      },
      {
        label: 'Last Name',
        required: true,
        persist: true,
        type: 'input',
        field: 'Last',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Last Name.' }],
        },
      },
      {
        label: 'Suffix',
        required: false,
        persist: true,
        type: 'input',
        field: 'Suffix',
      },
      {
        checkboxLabel: 'Informal Name Same as First Name',
        persist: false,
        defaultChecked: true,
        type: 'informalNameCheckbox',
        field: 'InformalNameSameAsName',
      },
      {
        label: 'Informal Name',
        required: true,
        persist: true,
        type: 'input',
        field: 'Informal',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Informal Name.' }],
        },
      },
      {
        label: 'Job Title',
        required: true,
        persist: true,
        type: 'input',
        field: 'Title',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Title.' }],
        },
      },
    ],
    [
      {
        label: 'Company',
        required: true,
        persist: true,
        type: 'input',
        field: 'Company',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Company.' }],
        },
      },
      {
        label: 'Country',
        required: true,
        persist: true,
        type: 'inputCountry',
        field: 'CountryCode',
        options: Countries,
        optionId: 'CountryCode',
        optionDesc: 'CountryDescription',
        formOverrides: {
          rules: [{ required: true, message: 'Select Country.' }],
        },
        extra:
          "Note: If your country does not appear in the list, select 'Other' and enter the country name in the box that appears below.",
      },
      {
        label: 'Corporate Address',
        required: true,
        persist: true,
        type: 'input',
        field: 'StreetAddress1',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Corporate Address.' }],
        },
        extra:
            "Note: No home addresses."
      },
      {
        label: 'Suite',
        required: false,
        persist: true,
        type: 'input',
        field: 'StreetAddress2',
      },
      {
        label: 'City',
        required: true,
        persist: true,
        type: 'input',
        field: 'City',
        formOverrides: {
          rules: [{ required: true, message: 'Enter City.' }],
        },
      },
      {
        label: 'State',
        required: true,
        persist: true,
        type: 'inputOfficeState',
        field: 'StateCode',
        options: States,
        optionId: 'StateCode',
        optionDesc: 'StateDescription',
        formOverrides: {
          rules: [{ required: true, message: 'Select State.' }],
        },
      },
      {
        label: 'Postal Code',
        required: true,
        persist: true,
        type: 'input',
        field: 'PostalCode',
        formOverrides: {
          rules: [{ required: true, message: 'Enter Postal Code' }],
        },
      },
      // {
      //   label: 'Mailing Address',
      //   checkboxLabel: 'Same as Office Address',
      //   required: false,
      //   persist: false,
      //   type: 'checkbox',
      // },
      // {
      //   label: 'P.O. Box Number',
      //   meta: 'extra',
      //   required: true,
      //   persist: true,
      //   type: 'input',
      //   field: 'PoBoxNumber',
      //   isMailing: true,
      //   formOverrides: {
      //     rules: [{ required: true, message: 'Enter P.O. Box Number.' }],
      //   },
      // },
      // {
      //   label: 'City',
      //   meta: 'extra',
      //   required: true,
      //   persist: true,
      //   type: 'input',
      //   field: 'PoBoxCity',
      //   isMailing: true,
      //   formOverrides: {
      //     rules: [{ required: true, message: 'Enter City.' }],
      //   },
      // },
      // {
      //   label: 'State',
      //   required: true,
      //   persist: true,
      //   type: 'inputMailingState',
      //   field: 'PoBoxStateCode',
      //   options: States,
      //   optionId: 'StateCode',
      //   optionDesc: 'StateDescription',
      //   isMailing: true,
      //   formOverrides: {
      //     rules: [{ required: true, message: 'Select State.' }],
      //   },
      // },
      // {
      //   label: 'Postal Code',
      //   meta: 'extra',
      //   required: true,
      //   persist: true,
      //   type: 'input',
      //   field: 'PoBoxPostalCode',
      //   isMailing: true,
      //   formOverrides: {
      //     rules: [{ required: true, message: 'Enter Postal Code.' }],
      //   },
      // },
      {
        label: 'Phone',
        required: true,
        persist: true,
        type: 'input',
        field: 'Number',
        formOverrides: {
          rules: [
            {
              required: true,
              pattern: new RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/),
              message: 'Enter Phone Number.',
            },
          ],
        },
      },
      {
        label: 'Extension',
        required: false,
        persist: true,
        type: 'input',
        field: 'Extension',
      },
      {
        label: 'Mobile',
        required: false,
        persist: true,
        type: 'input',
        field: 'Mobile',
        formOverrides: {
          rules: [
            {
              required: false,
              pattern: new RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/),
              message: 'Enter A Valid Mobile.',
            },
          ],
        },
      },
      // {
      //   label: 'Fax',
      //   required: true,
      //   persist: true,
      //   type: 'input',
      //   field: 'FaxNumber',
      //   formOverrides: {
      //     rules: [
      //       {
      //         required: false,
      //         pattern: new RegExp(/^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/),
      //         message: 'Enter A Valid Fax.',
      //       },
      //     ],
      //   },
      // },
    ],
    [
      {
        label: 'Email',
        required: true,
        persist: true,
        type: 'inputEmail',
        field: 'EmailAddress',
        inputType: 'email',
      },
      {
        label: 'Password',
        type: 'inputPassword',
        inputType: 'password',
        field: 'Password',
      },

      {
        label: 'Comments',
        type: 'adminComment',
        inputType: 'comments',
        field: 'Comments',
      },
    ],
  ];

  return (
    <Layout>
      <AdminLink pageID="504" pageType="core" />
      <Section bgColor="gray" halfPad>
        <Container>
          <Breadcrumbs theme="dark" />
          <div css={styles.container}>
            <div css={styles.content}>
              <RegisterHero
                title={designPageData.HeroHeadline}
                subtitle={componentParagraph1.Paragraph}
              />
            </div>
            <div css={styles.form}>
              <RegisterForm
                fieldInfo={fieldInfo}
                formTitle={paragraph2.BlockTitle}
                formSubtitle={componentParagraph2.Paragraph}
              />
            </div>
          </div>
        </Container>
      </Section>
      <WebLogImage browserPath pagedata={pagedata} />
    </Layout>
  );
};

export default Register;
